import React, { type ComponentType } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, RefreshCw, Database, Map, Play, MousePointerClick, Ear, ArrowLeftRight, Wrench, Sparkles, MonitorPlay, Mail, Smartphone, Users, Clock3, Globe2, Radio, Eye, Zap, ArrowLeft } from 'lucide-react';
const CLIENT_LOGOS = [{
  name: 'Roche',
  src: 'https://cdn.magicpatterns.com/uploads/oKM7AS6Say1ivXnufTeK4Q/Screenshot_2026-08-20_at_21.22.20.png'
}, {
  name: 'GSK',
  src: 'https://cdn.magicpatterns.com/uploads/chs5wo4p9K7rYZAXrMym5b/Screenshot_2026-08-20_at_21.22.24.png'
}, {
  name: 'Boehringer Ingelheim',
  src: 'https://cdn.magicpatterns.com/uploads/d5bawJHBsQSTkegSnvrVuj/Screenshot_2026-08-20_at_21.22.30.png'
}, {
  name: 'LEO Pharma',
  src: 'https://cdn.magicpatterns.com/uploads/pBTS69wAMQQDX9D3vkk2Rb/Screenshot_2026-08-20_at_21.22.35.png'
}, {
  name: 'Novartis',
  src: 'https://cdn.magicpatterns.com/uploads/uUutn2zPPVgFnTSiUdRWCm/Screenshot_2026-08-20_at_21.22.40.png'
}, {
  name: 'CSL',
  src: 'https://cdn.magicpatterns.com/uploads/pJnRcBrTsRx7HkvxQKP8x4/Screenshot_2026-08-20_at_21.23.27.png'
}, {
  name: 'Bayer',
  src: 'https://cdn.magicpatterns.com/uploads/gfWrjGPFiTnQ1dPmioRizg/Screenshot_2026-08-20_at_21.23.50.png'
}, {
  name: 'AbbVie',
  src: 'https://cdn.magicpatterns.com/uploads/68jEZZhKCb8PVjShWy7xh7/Screenshot_2026-08-20_at_21.24.11.png'
}];

// Reusable slide wrapper to enforce 16:9 safe areas, consistent padding, and navigation
function SlideWrapper({
  children,
  className = '',
  page,
  fullBleed = false,
  hideLogo = false
}: {
  children: React.ReactNode;
  className?: string;
  page?: number;
  fullBleed?: boolean;
  hideLogo?: boolean;
}) {
  const paddingClass = fullBleed ? 'p-0' : 'px-[6%] pb-[6%] pt-[12%]';
  return <div className={`relative flex h-full w-full flex-col overflow-hidden bg-[#FAFAFA] text-slate-900 ${paddingClass} ${className}`} data-id="element-4" data-mp="1iq2rld3d7ipr">
      {!hideLogo && <div className="absolute left-[6%] top-[5%] z-50" data-id="element-5" data-mp="3d32bvjiox13d">
          <img src="https://cdn.magicpatterns.com/uploads/cYecvHgbFGxT9gjSDQWm9C/snakker_logo_new2.png" alt="Snakker Logo" className="w-[120px] object-contain" data-id="element-6" data-mp="3d2j2ir73vl0y" />
        </div>}
      {children}
      {page && <div className="absolute bottom-6 right-8 z-50 flex items-center gap-4 text-[clamp(12px,1vw,16px)] font-medium text-slate-400" data-id="element-7" data-mp="ky4ql714m1ry">
          <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'ArrowLeft'
      }))} className="hover:text-[var(--brand)] transition-colors" aria-label="Previous slide" data-id="element-8" data-mp="bvvj6q15mois">
            <ArrowLeft className="h-5 w-5" data-id="element-9" data-mp="14kkjimudypth" />
          </button>
          <span data-id="element-10" data-mp="zs0xr2ljoodl">{page} / 12</span>
          <button onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'ArrowRight'
      }))} className="hover:text-[var(--brand)] transition-colors" aria-label="Next slide" data-id="element-11" data-mp="zr81ivk7ph9p">
            <ArrowRight className="h-5 w-5" data-id="element-12" data-mp="1niwvshglgfta" />
          </button>
        </div>}
    </div>;
}

// Reusable staggered fade-in container
function StaggerContainer({
  children,
  className = '',
  delay = 0.2
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  return <motion.div initial="hidden" animate="visible" variants={{
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: delay
      }
    }
  }} className={className} data-id="element-13" data-mp="24rz6mivj3d8v">
      {children}
    </motion.div>;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut'
    }
  }
};

// --- SLIDES ---

function Slide0_MeetSnakker() {
  return <SlideWrapper page={1} data-id="element-14" data-mp="2si1usnwbnz27">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-8" data-id="element-15" data-mp="2sg8m7325f4rk">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex w-[50%] flex-col justify-center" data-id="element-16" data-mp="2wjwu5mup02dc">
          <h2 className="text-[clamp(40px,5vw,80px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-17" data-mp="2nug6kd3ziktu">
            HCPs don’t lack content.<br data-id="element-18" data-mp="2jrtj0oecjm2g" />
            <span className="text-[var(--brand)]" data-id="element-19" data-mp="2xlb2yaay30t0">They lack time.</span>
          </h2>

          <p className="mt-8 text-[clamp(20px,2.5vw,36px)] font-medium leading-snug text-[var(--muted)]" data-id="element-20" data-mp="2xn5eiy0btgkf">
            Snakker creates Snaks™.<br data-id="element-21" data-mp="24w3tig9ycqsz" />
            Short, sharp, and satisfying stories engineered for attention.
          </p>

          <div className="mt-12" data-id="element-22" data-mp="20vtph95q0agr">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--brand)]" data-id="element-23" data-mp="20wmofnyftvoc">Trusted by</p>
            <div className="mt-4 flex flex-wrap gap-4" data-id="element-24" data-mp="2ew4j2jyf035m">
              {CLIENT_LOGOS.slice(0, 5).map(client => <img key={client.name} src={client.src} alt={client.name} className="h-8 object-contain opacity-60 grayscale" data-id="element-25" data-mp="29y2zzz0zo3u5" />)}
            </div>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex h-full min-h-0 w-[45%] items-center justify-end" data-id="element-26" data-mp="3ld9hd35oxcad">
          <img src="https://cdn.magicpatterns.com/uploads/6mnx8ZCX32yC1VvG3s2hca/image.png" alt="Hemlibra Snak Example" className="h-full w-full object-contain drop-shadow-2xl" data-id="element-27" data-mp="y3hhlodm9pmi" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide0a_FAQ() {
  return <SlideWrapper page={2} className="flex flex-col" data-id="element-28" data-mp="1us6z3dlc0g7y">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="flex-shrink-0 text-[clamp(40px,5vw,80px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-29" data-mp="1h1k2bqz4t0ei">
        FAQ: <span className="text-[var(--brand)]" data-id="element-30" data-mp="29oo8hz9ynkrv">What exactly is a Snak?</span>
      </motion.h2>

      <StaggerContainer className="mt-12 flex min-h-0 flex-1 flex-col gap-8" data-id="element-31" data-mp="2ei8x50pnz2ee">
        <motion.div variants={fadeUp} className="flex flex-col border-l-4 border-[var(--brand)] pl-6" data-id="element-32" data-mp="2obmyrqx30950">
          <h3 className="text-[clamp(24px,3vw,40px)] font-bold text-[var(--fg)]" data-id="element-33" data-mp="2jewk980xdllp">Is it a new software or channel?</h3>
          <p className="mt-2 text-[clamp(18px,2.2vw,32px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-34" data-mp="2ecnne0kxlzeb">
            No. It’s a <strong className="text-[var(--fg)]" data-id="element-35" data-mp="29hrteuw4x1yk">channel-agnostic communication format</strong> delivered via a simple URL. It doesn't replace your existing channels; it upgrades the content within them.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="flex flex-col border-l-4 border-[var(--brand)] pl-6" data-id="element-36" data-mp="1vn7lp9xig0wb">
          <h3 className="text-[clamp(24px,3vw,40px)] font-bold text-[var(--fg)]" data-id="element-37" data-mp="1qppbzhbo5hn9">When should we use it?</h3>
          <p className="mt-2 text-[clamp(18px,2.2vw,32px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-38" data-mp="1loru75ukmpma">
            Whenever you need <strong className="text-[var(--fg)]" data-id="element-39" data-mp="1hp15r8siolkx">active engagement and valuable data feedback</strong>. If you just need to store reference material, use a PDF. If you need a dialogue, use a Snak.
          </p>
        </motion.div>

        <motion.div variants={fadeUp} className="flex flex-col border-l-4 border-[var(--brand)] pl-6" data-id="element-40" data-mp="66vjgwelabj5">
          <h3 className="text-[clamp(24px,3vw,40px)] font-bold text-[var(--fg)]" data-id="element-41" data-mp="b5q13v0tlu1o">Where does it fit in our strategy?</h3>
          <p className="mt-2 text-[clamp(18px,2.2vw,32px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-42" data-mp="39odeu4d6dq2o">
            <strong className="text-[var(--fg)]" data-id="element-43" data-mp="3enqe5ol8gpm5">Everywhere.</strong> Because it's a link, it's highly effective for email outreach, face-to-face Veeva details, congress booths, and website "Snakkerfication."
          </p>
        </motion.div>
      </StaggerContainer>
    </SlideWrapper>;
}
function Slide0b_WhatIsASnakFormat() {
  return <SlideWrapper className="flex flex-col" page={3} data-id="element-44" data-mp="2qv04jm0syif1">
      <motion.header initial={{
      opacity: 0,
      y: 18
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="flex-shrink-0 text-center" data-id="element-45" data-mp="2vvfiaeozxrxj">
        <h2 className="text-[clamp(40px,5vw,80px)] font-bold tracking-tight text-[var(--fg)]" data-id="element-46" data-mp="1kajxp4wio0bo">
          What is a <span className="text-[var(--brand)]" data-id="element-47" data-mp="1yama4bm221jj">Snak?</span>
        </h2>
        <p className="mt-4 text-[clamp(20px,2.5vw,36px)] font-medium text-[var(--muted)]" data-id="element-48" data-mp="1taq466tss01c">
          A format to explain complex issues in a short, satisfying way.
        </p>
      </motion.header>

      <StaggerContainer className="mt-10 grid min-h-0 flex-1 grid-cols-3 gap-8 pb-4" data-id="element-49" data-mp="238w5am7s5255">
        <motion.div variants={fadeUp} className="flex min-h-0 flex-col items-center text-center" data-id="element-50" data-mp="2h01ae3tx2mmv">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-inner" data-id="element-51" data-mp="2cyfppwb55424">
            <img src="https://cdn.magicpatterns.com/uploads/9PcYYi1bjYDtkFKLHb4HXN/Screenshot_2026-08-24_at_10.20.50.png" alt="Highly visual" className="h-full w-full object-cover" data-id="element-52" data-mp="2qza18nthjl50" />
          </div>
          <h3 className="mt-6 flex-shrink-0 text-[clamp(20px,2.5vw,32px)] font-bold text-[var(--fg)]" data-id="element-53" data-mp="35lyz6l839q9a">Highly visual</h3>
          <p className="mt-2 flex-shrink-0 text-[clamp(14px,1.5vw,20px)] text-[var(--muted)]" data-id="element-54" data-mp="3ajibvgp51uz4">Understood at a glance.</p>
        </motion.div>

        <motion.div variants={fadeUp} className="flex min-h-0 flex-col items-center text-center" data-id="element-55" data-mp="30luf6l7z2deb">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-inner" data-id="element-56" data-mp="2mufywm5g8qay">
            <img src="https://cdn.magicpatterns.com/uploads/2H17bwrMVXk3HzB54QFkf7/Screenshot_2026-08-24_at_10.19.16.png" alt="Adaptive" className="h-full w-full object-cover object-left" data-id="element-57" data-mp="2rm7p2vkp0u0k" />
          </div>
          <h3 className="mt-6 flex-shrink-0 text-[clamp(20px,2.5vw,32px)] font-bold text-[var(--fg)]" data-id="element-58" data-mp="2hwebjp0zq4to">Adaptive</h3>
          <p className="mt-2 flex-shrink-0 text-[clamp(14px,1.5vw,20px)] text-[var(--muted)]" data-id="element-59" data-mp="23w3lupbf2wa3">Reads signals and adjusts.</p>
        </motion.div>

        <motion.div variants={fadeUp} className="flex min-h-0 flex-col items-center text-center" data-id="element-60" data-mp="td5h0hrvxpt8">
          <div className="flex min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-2xl bg-slate-100 shadow-inner" data-id="element-61" data-mp="ybp5aotybdor">
            <img src="https://cdn.magicpatterns.com/uploads/w1FNxYqhnWeramphNUuzps/Screenshot_2026-08-24_at_10.16.43.png" alt="Satisfying" className="h-full w-full object-cover object-top" data-id="element-62" data-mp="kdiqws4lw6fh" />
          </div>
          <h3 className="mt-6 flex-shrink-0 text-[clamp(20px,2.5vw,32px)] font-bold text-[var(--fg)]" data-id="element-63" data-mp="1c4oi2j2cmtsm">Satisfying</h3>
          <p className="mt-2 flex-shrink-0 text-[clamp(14px,1.5vw,20px)] text-[var(--muted)]" data-id="element-64" data-mp="1h1ooiv341yk9">Rewards the attention invested.</p>
        </motion.div>
      </StaggerContainer>
    </SlideWrapper>;
}
function Slide1_Problem() {
  return <SlideWrapper page={4} data-id="element-65" data-mp="1v2hnbs0g3ft6">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-8" data-id="element-66" data-mp="1v16j5r33861j">
        {/* Left Column: Text & Doorway */}
        <div className="flex h-full min-h-0 w-[30%] flex-col justify-between py-4" data-id="element-67" data-mp="1zuiy19hysyh3">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="flex-shrink-0 text-[clamp(28px,3.5vw,56px)] font-bold leading-[1.1] tracking-tight" data-id="element-68" data-mp="3ki21rgw0gbqm">
            Pharma has plenty of science.<br data-id="element-69" data-mp="3p9tiz0ta1543" />
            <span className="text-[var(--muted)]" data-id="element-70" data-mp="3pbeeasnf0s30">The challenge is getting people into it.</span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.8,
          duration: 0.6
        }} className="my-auto flex aspect-square w-[60%] max-w-[180px] flex-shrink-0 flex-col items-center justify-center rounded-2xl bg-[var(--brand)] shadow-2xl" data-id="element-71" data-mp="31kr5841rjwev">
            <span className="text-[clamp(14px,1.8vw,24px)] font-bold tracking-widest text-white" data-id="element-72" data-mp="36lyds5bqczml">ATTENTION</span>
            <ArrowRight className="mt-2 h-6 w-6 text-white/50" data-id="element-73" data-mp="ltd081rcul4e" />
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2
        }} className="flex-shrink-0 text-[clamp(18px,2vw,28px)] font-medium leading-snug text-[var(--brand)]" data-id="element-74" data-mp="lvpea3tzsbc6">
            You have to earn the right to get to the scientific conversation.
          </motion.p>
        </div>

        {/* Right Column: Massive Video */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.4,
        duration: 0.8
      }} className="relative flex h-full min-h-0 w-[70%] items-center justify-center overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-slate-200" data-id="element-75" data-mp="17rr25aag2414">
          <video src="https://cdn.magicpatterns.com/uploads/57ZMHM6UpgVeWweLj1VZwf/affinity.mp4" autoPlay loop muted playsInline className="h-full w-full object-contain" data-id="element-76" data-mp="ouzc8i0ecqo3" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide3_Episodic() {
  return <SlideWrapper page={5} className="flex flex-col" data-id="element-77" data-mp="29nn0blig6lpc">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-shrink-0 text-[clamp(40px,5vw,80px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-78" data-mp="25nmf9i0ufc9b">
        Don’t compress the 'movie'.<br data-id="element-79" data-mp="1vodqd3ku4ntt" />
        <span className="text-[var(--brand)]" data-id="element-80" data-mp="1vox6qfuu745q">Turn it into a series.</span>
      </motion.h2>

      <div className="mt-8 flex min-h-0 flex-1 items-center justify-between gap-8" data-id="element-81" data-mp="1qrn767dqhc46">
        {/* Left: Monolith */}
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3
      }} className="relative flex h-full min-h-0 w-[25%] flex-col items-center justify-center overflow-hidden rounded-xl bg-slate-200 shadow-inner ring-1 ring-slate-200" data-id="element-82" data-mp="1lx9a80jfawxe">
          <img src="https://cdn.magicpatterns.com/uploads/2B25zz3xzXz3iLPoG2wQgS/Screenshot_2026-08-24_at_11.36.13.png" alt="Dense Content Collection" className="h-full w-full object-cover opacity-90" data-id="element-83" data-mp="181f9w5wptfvw" />
          <div className="absolute inset-0 bg-black/10" data-id="element-84" data-mp="380sl62zzappc"></div>
        </motion.div>

        <ArrowRight className="h-10 w-10 flex-shrink-0 text-slate-300" data-id="element-85" data-mp="2tdhd4b309ntz" />

        {/* Right: Episodes */}
        <div className="flex h-full min-h-0 w-[65%] flex-col justify-between gap-4" data-id="element-86" data-mp="2yb914u0j4psw">
          <StaggerContainer className="flex min-h-0 flex-1 gap-4" delay={0.6} data-id="element-87" data-mp="2kbq7t7cdnj0n">
            {["https://cdn.magicpatterns.com/uploads/f9VXuGD5iawbzpT2bm6J7d/Screenshot_2026-08-24_at_11.26.03.png", "https://cdn.magicpatterns.com/uploads/8VHtVncWAcRapn6m1kL1Bf/Screenshot_2026-08-24_at_11.26.17.png", "https://cdn.magicpatterns.com/uploads/43QqFtN3wAVVwSnDtbqEPa/Screenshot_2026-08-24_at_11.26.30.png", "https://cdn.magicpatterns.com/uploads/6eJdzXtDSKTjtFwn4nfj6E/Screenshot_2026-08-24_at_11.26.23.png"].map((src, i) => <motion.div key={i} variants={fadeUp} className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl shadow-xl" data-id="element-88" data-mp="1y3kqri5q89bi" data-mpi={i}>
                <img src={src} alt={`Episode ${i + 1}`} className="h-full w-full object-cover" data-id="element-89" data-mp="1t62ebhsi3byr" />
              </motion.div>)}
          </StaggerContainer>
          
          {/* Suspense Curve */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.2
        }} className="relative mt-2 h-[12%] min-h-[40px] w-full flex-shrink-0" data-id="element-90" data-mp="231clejxra9je">
            <svg viewBox="0 0 400 50" className="h-full w-full overflow-visible fill-none stroke-[var(--brand)] stroke-[3px]" data-id="element-91" data-mp="2csgy1czylib2">
              <path d="M 0,50 Q 25,0 50,25 T 100,50 Q 125,0 150,25 T 200,50 Q 225,0 250,25 T 300,50 Q 325,0 350,25 T 400,50" data-id="element-92" data-mp="2ltf37ym4vvv8" />
            </svg>
            <div className="absolute inset-0 flex justify-between px-[12.5%] text-[clamp(12px,1vw,16px)] font-medium text-slate-400" data-id="element-93" data-mp="wfjp09yfrv3q">
              <span className="mt-auto" data-id="element-94" data-mp="rhi91xwb4bwh">Hook</span><span className="mt-auto" data-id="element-95" data-mp="2ri6nptp3srgi">Hook</span><span className="mt-auto" data-id="element-96" data-mp="2riyhr031yh4o">Hook</span><span className="mt-auto" data-id="element-97" data-mp="2rjzxviyxdtt9">Resolve</span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mt-8 flex flex-shrink-0 items-end justify-between" data-id="element-98" data-mp="30ilbczpjiin6">
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.5
      }} className="max-w-[50%] text-[clamp(18px,2vw,32px)] font-medium text-slate-700" data-id="element-99" data-mp="30kfuq9l5y2p5">
          Each episode resolves one idea while creating a reason to continue.
        </motion.p>
        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1.7
      }} className="max-w-[40%] text-right text-[clamp(14px,1.5vw,24px)] text-slate-500" data-id="element-100" data-mp="3fe18jdlf2uns">
          The story progresses step by step from what the HCP understands today to where the brand needs to take them.
        </motion.p>
      </div>
    </SlideWrapper>;
}
function Slide4_Anatomy() {
  return <SlideWrapper className="items-center justify-center bg-white" page={6} fullBleed hideLogo data-id="element-101" data-mp="21hfglehp5q2">
      <motion.img initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8
    }} src="https://cdn.magicpatterns.com/uploads/imAonTm4u7sTnwCkc3v2Z4/Screenshot_2026-08-24_at_11.28.54.png" alt="The Anatomy of a Snak" className="h-full w-full object-contain" data-id="element-102" data-mp="c5fo5n3rqo76" />
    </SlideWrapper>;
}
function Slide5_Satisfying() {
  return <SlideWrapper className="flex flex-col justify-center" page={7} data-id="element-103" data-mp="uvdv1zvtyb98">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-12" data-id="element-104" data-mp="pw16napb8w4j">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex w-[45%] flex-col justify-center" data-id="element-105" data-mp="py460lccs1lx">
          <h2 className="flex-shrink-0 text-[clamp(40px,5vw,80px)] font-bold tracking-tight text-[var(--fg)]" data-id="element-106" data-mp="1dlltw5shyfjm">
            The reward is not a prize.
          </h2>
          <p className="mt-8 flex-shrink-0 text-[clamp(24px,3vw,48px)] font-medium leading-snug text-[var(--muted)]" data-id="element-107" data-mp="18tly8pjgm8su">
            Interaction is part of the story.<br data-id="element-108" data-mp="1xa5yapnvg347" />
            <span className="font-bold text-[var(--brand)]" data-id="element-109" data-mp="1xapeo1xvijg4">Not decoration.</span>
          </p>
          <div className="mt-12 flex flex-col gap-6 text-[clamp(18px,2vw,28px)] font-medium text-[var(--muted)]" data-id="element-110" data-mp="1ilj97w8fzwzs">
            <p data-id="element-111" data-mp="1nkmf1iw8y78t">✓ "Was I right?" (Prediction)</p>
            <p data-id="element-112" data-mp="2b9fbdggs6h3y">✓ "I didn't know that." (Surprise)</p>
            <p data-id="element-113" data-mp="2baqd7aqo1esf">✓ "What do others think?" (Peer insight)</p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex h-full min-h-0 w-[55%] items-center justify-center" data-id="element-114" data-mp="2g7758g0zkdas">
          <img src="https://cdn.magicpatterns.com/uploads/w1FNxYqhnWeramphNUuzps/Screenshot_2026-08-24_at_10.16.43.png" alt="Peer Comparison UI" className="h-full w-full object-contain drop-shadow-2xl" data-id="element-115" data-mp="pw2l3aruv29d" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide6_ListensBack() {
  return <SlideWrapper page={8} className="flex flex-col" data-id="element-116" data-mp="343bsizdrrup8">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-shrink-0 text-[clamp(36px,4.5vw,72px)] font-bold leading-[1.1] tracking-tight" data-id="element-117" data-mp="3i3cwwot8tk9g">
        Traditional content talks.<br data-id="element-118" data-mp="2qdi42i66fcg1" />
        <span className="text-[var(--brand)]" data-id="element-119" data-mp="2qcylcz8pck0y">A Snak listens back.</span>
      </motion.h2>

      <div className="mt-6 flex min-h-0 w-full flex-1 items-center justify-center rounded-2xl bg-white p-2 shadow-inner ring-1 ring-slate-200" data-id="element-120" data-mp="2lenaqmal25ho">
        <motion.img initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} src="https://cdn.magicpatterns.com/uploads/7vd1ViVemKP8aiuT1hfYYr/Screenshot_2026-08-24_at_10.19.50.png" alt="Branching Logic Data" className="h-full w-full object-contain" data-id="element-121" data-mp="2z6lbo9u54v2r" />
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.8
    }} className="mt-6 flex flex-shrink-0 items-center justify-between" data-id="element-122" data-mp="woy9i5ql7zjk">
        <p className="text-[clamp(20px,2.5vw,36px)] font-medium text-[var(--fg)]" data-id="element-123" data-mp="16ft01mav9lat">
          The next story is informed by the last one.
        </p>
        <div className="flex items-center gap-4 text-[clamp(14px,1.8vw,20px)] font-bold tracking-widest text-[var(--brand)]" data-id="element-124" data-mp="1gc60r44twzf2">
          <span data-id="element-125" data-mp="1bct3dpgc09y8">TELL</span> <ArrowRight className="h-5 w-5" data-id="element-126" data-mp="1bdb9ddssq2jc" />
          <span data-id="element-127" data-mp="1pd3vswxahwli">LISTEN</span> <ArrowRight className="h-5 w-5" data-id="element-128" data-mp="1pdm2knhkvt7o" />
          <span data-id="element-129" data-mp="1pb9m6ergxqwq">ADAPT</span>
        </div>
      </motion.div>
    </SlideWrapper>;
}
function Slide7_WhySnakker() {
  return <SlideWrapper page={9} className="flex flex-col justify-between" data-id="element-130" data-mp="3r4qod2wtxzk3">
      <motion.h2 initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="flex-shrink-0 text-[clamp(36px,4.5vw,72px)] font-bold leading-[1.1] tracking-tight" data-id="element-131" data-mp="3r6tf5p5t3wpv">
        The tool was never the difficult part.<br data-id="element-132" data-mp="26n7te02lr4yn" />
        <span className="text-[var(--brand)]" data-id="element-133" data-mp="26my3sdlgyzth">The story was.</span>
      </motion.h2>

      <div className="mt-6 flex min-h-0 w-full flex-1 gap-8" data-id="element-134" data-mp="1svtadt8p33rl">
        <StaggerContainer className="flex min-h-0 w-[35%] flex-col gap-4" data-id="element-135" data-mp="1st721rdzck8b">
          <motion.div variants={fadeUp} className="flex flex-shrink-0 flex-col items-center justify-center rounded-2xl bg-slate-100 p-4 text-center" data-id="element-136" data-mp="1wvbtqkkvb6sb">
            <Wrench className="mb-2 h-8 w-8 text-slate-400" data-id="element-137" data-mp="1nvx6rv3wteck" />
            <span className="text-[clamp(14px,1.8vw,24px)] font-bold tracking-widest text-slate-400" data-id="element-138" data-mp="1nxi1bkq84xaf">TECHNOLOGY</span>
          </motion.div>
          <motion.div variants={fadeUp} className="relative flex min-h-0 flex-1 overflow-hidden rounded-2xl bg-[#1a1512] shadow-xl ring-1 ring-slate-200" data-id="element-139" data-mp="1dxr7ll9hmmbe">
            <img src="https://cdn.magicpatterns.com/uploads/3qgikYAL5ZQnnZa5vdot2Q/Screenshot_2026-08-24_at_11.58.03.png" alt="Storytelling" className="h-full w-full object-contain" data-id="element-140" data-mp="34b83un1p3dhy" />
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/40" data-id="element-141" data-mp="g4rgc3hnwcba">
              <span className="text-[clamp(14px,1.8vw,24px)] font-bold tracking-widest text-white drop-shadow-md" data-id="element-142" data-mp="c2ecjkmh6oh5">STORY</span>
            </div>
          </motion.div>
        </StaggerContainer>

        <StaggerContainer className="flex min-h-0 w-[65%] flex-col justify-center gap-4 py-2" delay={0.6} data-id="element-143" data-mp="13tua29pklvm5">
          {[{
          role: 'ABBVIE',
          desc: 'Owns the science, evidence, objective and market knowledge'
        }, {
          role: 'SNAKKER',
          desc: 'Finds the story, constructs the narrative and produces the experience'
        }, {
          role: 'AI',
          desc: 'Makes the craftsmanship scalable across brands, campaigns and countries'
        }].map((item, i) => <motion.div key={i} variants={fadeUp} className="flex items-center gap-6 border-b border-slate-200 pb-4 last:border-0 last:pb-0" data-id="element-144" data-mp="1nk5wjfzaugw6" data-mpi={i}>
              <span className="w-[100px] flex-shrink-0 text-[clamp(14px,1.8vw,20px)] font-black tracking-widest text-slate-900" data-id="element-145" data-mp="1q3arrpuddwy6">{item.role}</span>
              <span className="text-[clamp(14px,1.8vw,24px)] text-slate-600" data-id="element-146" data-mp="1v3oowcufnmhf">{item.desc}</span>
            </motion.div>)}
        </StaggerContainer>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      delay: 1.5
    }} className="mt-6 flex-shrink-0 text-center" data-id="element-147" data-mp="24320bb24al4s">
        <p className="text-[clamp(18px,2vw,32px)] font-medium leading-tight text-slate-800" data-id="element-148" data-mp="2e06pzkv68yqc">
          We don’t turn scientific brand managers into storytellers.<br data-id="element-149" data-mp="2ironu01wej73" />
          <span className="font-bold text-[var(--brand)]" data-id="element-150" data-mp="2it9j5rw1e660">We give them a storytelling capability.</span>
        </p>
      </motion.div>
    </SlideWrapper>;
}
function Slide8_Website() {
  return <SlideWrapper page={10} className="flex flex-col" data-id="element-151" data-mp="2rps1ka6n6boc">
      <div className="flex min-h-0 flex-1 items-center justify-between gap-12" data-id="element-152" data-mp="2wrzi7d2f2fx0">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 0.6
      }} className="flex h-full min-h-0 w-[45%] flex-col justify-center" data-id="element-153" data-mp="2j3pcfxlh8j70">
          <h2 className="flex-shrink-0 text-[clamp(36px,4.5vw,72px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-154" data-mp="2sspfpt70i0r0">
            Reverse the traditional<br data-id="element-155" data-mp="205d5mii1flel" />
            <span className="text-[var(--brand)]" data-id="element-156" data-mp="204tlcv4x0kxe">website experience.</span>
          </h2>
          
          <div className="mt-8 flex min-h-0 flex-col gap-4 overflow-y-auto text-[clamp(16px,1.8vw,24px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-157" data-mp="1w49lvkwvd583">
            <p data-id="element-158" data-mp="2a1g5fhpf37yd">
              <strong className="text-[var(--fg)]" data-id="element-159" data-mp="254fwmz16nr3k">Start lean-back.</strong> Begin with an animated introduction that requires almost no effort, but quickly delivers value.
            </p>
            <p data-id="element-160" data-mp="3lkiurhbc4g6m">
              <strong className="text-[var(--fg)]" data-id="element-161" data-mp="3gjlb10aofe30">Gradually introduce depth.</strong> As users stay and engage, move from a simple, guided flow into a more personalized experience.
            </p>
            <p data-id="element-162" data-mp="17ax4zefr3bd6">
              <strong className="text-[var(--fg)]" data-id="element-163" data-mp="17bgatws4m9au">Timing matters.</strong> Increase effort and complexity only when the user is ready for it.
            </p>
          </div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.3,
        duration: 0.8
      }} className="flex h-full min-h-0 w-[55%] items-center justify-center" data-id="element-164" data-mp="y9yvigmjmbn7">
          <img src="https://cdn.magicpatterns.com/uploads/juyDCwtopMRwntfuXQvPax/Screenshot_2026-08-24_at_11.04.26.png" alt="User Readiness vs Interaction Complexity Graph" className="h-full w-full object-contain drop-shadow-xl" data-id="element-165" data-mp="1q0mwnso7vh0v" />
        </motion.div>
      </div>
    </SlideWrapper>;
}
function Slide9_Congress() {
  return <SlideWrapper page={11} className="items-center justify-center bg-[#F9F9F9]" fullBleed hideLogo data-id="element-166" data-mp="29q73wyonoh6s">
      <motion.img initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.8
    }} src="https://cdn.magicpatterns.com/uploads/nDTcCpxqMi3fWVtFzEfjZs/Screenshot_2026-08-24_at_13.05.53.png" alt="The Congress Journey" className="h-full w-full object-cover" data-id="element-167" data-mp="2jk2xwafm7i01" />
    </SlideWrapper>;
}
function Slide10_Closeout() {
  return <SlideWrapper page={12} className="items-center justify-center text-center" data-id="element-168" data-mp="20mrxunm5skjj">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="flex max-w-[80%] flex-col items-center" data-id="element-169" data-mp="1vw36r9ee5pkx">
        <h2 className="text-[clamp(48px,6vw,96px)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-170" data-mp="1lxnh7av0iodj">
          Ready to rethink your <span className="text-[var(--brand)]" data-id="element-171" data-mp="1gyspfjf2hpha">engagement?</span>
        </h2>
        <p className="mt-8 text-[clamp(20px,2.5vw,36px)] font-medium leading-relaxed text-[var(--muted)]" data-id="element-172" data-mp="1cx7g281qrtbu">
          Discover how Snakker’s short, episodic storytelling can support your commercial and medical engagement strategies.
        </p>
        <button className="mt-12 rounded-full bg-[var(--brand)] px-10 py-5 text-[clamp(18px,2vw,28px)] font-bold text-white shadow-xl transition-transform hover:scale-105" data-id="element-173" data-mp="3skb98te6mf5f">
          Get in touch
        </button>
      </motion.div>
    </SlideWrapper>;
}
export const slides: DeckSlide[] = [{
  id: 'scr_odohm2',
  title: 'Meet Snakker',
  component: Slide0_MeetSnakker
}, {
  id: 'scr_b7yf4j',
  title: 'FAQ: What is a Snak?',
  component: Slide0a_FAQ
}, {
  id: 'scr_blr9pa',
  title: 'What is a Snak?',
  component: Slide0b_WhatIsASnakFormat
}, {
  id: 'scr_problem',
  title: 'The Problem',
  component: Slide1_Problem
}, {
  id: 'scr_episodic',
  title: 'Episodic Storytelling',
  component: Slide3_Episodic
}, {
  id: 'scr_anatomy',
  title: 'Anatomy of a Snak',
  component: Slide4_Anatomy
}, {
  id: 'scr_satisfying',
  title: 'What makes it satisfying?',
  component: Slide5_Satisfying
}, {
  id: 'scr_listens',
  title: 'The Story Listens Back',
  component: Slide6_ListensBack
}, {
  id: 'scr_whysnakker',
  title: 'Why Snakker?',
  component: Slide7_WhySnakker
}, {
  id: 'scr_website',
  title: 'Snakkerfication of Websites',
  component: Slide8_Website
}, {
  id: 'scr_r91cdb',
  title: 'The Congress Journey',
  component: Slide9_Congress
}, {
  id: 'scr_closeout',
  title: 'Get in Touch',
  component: Slide10_Closeout
}];